/**
 * Job-level metric calculators reused across ServiceJobForm,
 * ServiceJobsList, and the grouped Service reports.
 *
 *   • Job Hours  = Ramp Out − Ramp In        (formatted "Xd Yh" / "Yh")
 *   • Days Delay = Ramp In  − Vehicle In     (positive = late ramp-in,
 *                                              negative shown as "-Xd Yh")
 */

export const combineDateTime = (dateStr, timeStr) => {
  if (!dateStr) return null;

  // 1) Parse date — accepts DD-MM-YYYY, YYYY-MM-DD, DD/MM/YYYY, with or
  //    without padding. Rejects garbage cleanly.
  const dateParts = String(dateStr).trim().split(/[-/]/);
  if (dateParts.length !== 3) return null;
  let y, m, d;
  if (dateParts[0].length === 4) {
    y = dateParts[0]; m = dateParts[1]; d = dateParts[2];
  } else {
    d = dateParts[0]; m = dateParts[1]; y = dateParts[2];
  }
  if (y && y.length === 2) y = '20' + y;
  const yi = parseInt(y, 10);
  const mi = parseInt(m, 10);
  const di = parseInt(d, 10);
  if (!yi || !mi || !di || mi > 12 || di > 31) return null;

  // 2) Parse time — accepts ALL of:
  //      "10:30", "9:5", "10:30 ", "  10:30 "   (24-hour)
  //      "10:30:00"                              (with seconds, ignored)
  //      "5:56 PM", "5:56pm", "05:56 AM"         (12-hour, any case, optional space)
  //    Returns null only when input is provably non-numeric.
  let hh = 0, mm = 0;
  const timeRaw = String(timeStr || '').trim();
  if (timeRaw) {
    const m1 = timeRaw.match(/^\s*(\d{1,2})\s*:\s*(\d{1,2})(?:\s*:\s*\d{1,2})?\s*(am|pm)?\s*$/i);
    if (!m1) return null;
    hh = parseInt(m1[1], 10);
    mm = parseInt(m1[2], 10);
    const ap = (m1[3] || '').toLowerCase();
    if (ap === 'pm' && hh < 12) hh += 12;
    if (ap === 'am' && hh === 12) hh = 0;
    if (isNaN(hh) || isNaN(mm) || hh > 23 || mm > 59) return null;
  }

  const dt = new Date(
    `${yi}-${String(mi).padStart(2, '0')}-${String(di).padStart(2, '0')}` +
    `T${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:00`
  );
  return isNaN(dt.getTime()) ? null : dt;
};

export const calcJobHours = (rin, rinT, rout, routT) => {
  const a = combineDateTime(rin, rinT);
  const b = combineDateTime(rout, routT);
  if (!a || !b || b <= a) return '';
  const diffMs = b - a;
  const totalH = Math.floor(diffMs / 3600000);
  const days = Math.floor(totalH / 24);
  const hh = totalH % 24;
  return days > 0 ? `${days}d ${hh}h` : `${hh}h`;
};

export const calcDaysDelay = (vin, vinT, rin, rinT) => {
  const a = combineDateTime(vin, vinT);
  const b = combineDateTime(rin, rinT);
  if (!a || !b) return '';
  const diffMs = b - a;
  const totalH = Math.floor(diffMs / 3600000);
  if (totalH === 0 && diffMs >= 0) return '0h';
  if (totalH < 0) {
    const aH = Math.abs(totalH);
    const aD = Math.floor(aH / 24);
    return `-${aD}d ${aH % 24}h`;
  }
  const days = Math.floor(totalH / 24);
  return days > 0 ? `${days}d ${totalH % 24}h` : `${totalH}h`;
};

/** Convenience: compute both metrics directly from a service-job row. */
export const jobMetrics = (j) => ({
  jobHours: calcJobHours(j?.ramp_in_date, j?.ramp_in_time, j?.ramp_out_date, j?.ramp_out_time),
  daysDelay: calcDaysDelay(j?.vehicle_in_date, j?.vehicle_in_time, j?.ramp_in_date, j?.ramp_in_time),
});
